import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Download, ExternalLink } from 'lucide-react';
import BRAND from '../../config/branding';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const formatMoney = (value) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value || 0));

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '-');

const getInvoiceBillTo = (invoice = {}) => {
  const billingParty = invoice?.billingParty || {};
  const fallbackAddress =
    billingParty?.type === 'facility' ? invoice?.facilityId?.address : invoice?.clientId?.address;
  return {
    name:
      billingParty?.name ||
      (billingParty?.type === 'facility'
        ? invoice?.facilityId?.name || invoice?.clientId?.name
        : invoice?.clientId?.name) ||
      '-',
    addressLines: [
      billingParty?.address?.street || fallbackAddress?.street,
      [
        billingParty?.address?.city || fallbackAddress?.city,
        billingParty?.address?.state || fallbackAddress?.state,
        billingParty?.address?.zip || fallbackAddress?.zip,
      ]
        .filter(Boolean)
        .join(', '),
    ].filter(Boolean),
    contactLines: [
      billingParty?.contactName,
      billingParty?.contactEmail || invoice?.billingEmail,
      billingParty?.contactPhone,
      billingParty?.apEmail ? `AP: ${billingParty.apEmail}` : null,
    ].filter(Boolean),
  };
};

const buildInvoiceScopeStatement = (invoice = {}) => {
  const sourceType = String(invoice?.source?.type || '').trim();
  const hasCommercialBinding = sourceType === 'proposal_approved' || Number.isFinite(Number(invoice?.source?.pricingBundleVersion));
  if (!hasCommercialBinding) return '';

  const commercialFacilityName = String(invoice?.source?.commercialFacilityName || '').trim();
  const billingFacilityName = String(invoice?.source?.billingFacilityName || invoice?.facilityId?.name || '').trim();
  const organizationName = String(invoice?.clientId?.name || invoice?.billingParty?.name || 'this organization').trim();

  if (commercialFacilityName) {
    return `This invoice reflects the approved commercial package for ${commercialFacilityName} only. Any additional facilities require a separate expansion or revised commercial package.`;
  }

  if (invoice?.billingScope === 'facility' && billingFacilityName) {
    return `This invoice covers charges billed to ${billingFacilityName} under the organization's approved commercial package.`;
  }

  return `This invoice reflects the approved commercial package for ${organizationName} on an organization-wide basis.`;
};

export default function InvoicePublicPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [invoice, setInvoice] = useState(null);
  const [issuerProfile, setIssuerProfile] = useState({ issuerName: BRAND.name, issuerEmail: '' });
  const [downloading, setDownloading] = useState(false);
  const querySuffix = useMemo(() => (searchParams.get('preview') === '1' ? '?preview=1' : ''), [searchParams]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await fetch(`${API_BASE}/invoices/public/${token}${querySuffix}`);
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload?.message || payload?.error || 'Unable to load invoice link.');
        }
        if (cancelled) return;
        setInvoice(payload.invoice);
        setIssuerProfile({
          issuerName: payload?.issuerProfile?.issuerName || BRAND.name,
          issuerEmail: payload?.issuerProfile?.issuerEmail || '',
        });
      } catch (err) {
        if (!cancelled) setError(err.message || 'Unable to load invoice link.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [token, querySuffix]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await fetch(`${API_BASE}/invoices/public/${token}/pdf${querySuffix}`);
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.message || payload?.error || 'Failed to download invoice PDF.');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoice?.invoiceNumber || 'invoice'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Failed to download invoice PDF.');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-teal-700" />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full rounded-2xl bg-white border border-red-200 p-8 text-center">
          <AlertTriangle className="mx-auto text-red-500 mb-4" size={40} />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Invoice Link Unavailable</h1>
          <p className="text-gray-600 text-sm">{error || 'Please contact support for a new invoice link.'}</p>
        </div>
      </div>
    );
  }

  const billTo = getInvoiceBillTo(invoice);
  const invoiceScopeStatement = buildInvoiceScopeStatement(invoice);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-3xl mx-auto bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-teal-50 via-teal-100 to-teal-600 p-6">
          <img src={BRAND.logo} alt={`${BRAND.name} logo`} className="h-16 w-auto object-contain mb-2" />
          <h1 className="text-2xl font-bold text-slate-900">{issuerProfile.issuerName || BRAND.name}</h1>
          <p className="text-sm text-teal-900">Invoice {invoice.invoiceNumber}</p>
        </div>

        <div className="p-6 space-y-5">
          {error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
          ) : null}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Bill To</p>
              <p className="font-semibold text-gray-900">{billTo.name}</p>
              {billTo.addressLines.length > 0 ? (
                <p className="text-gray-600 whitespace-pre-line mt-1">{billTo.addressLines.join('\n')}</p>
              ) : null}
              {billTo.contactLines.length > 0 ? (
                <p className="text-gray-500 whitespace-pre-line mt-1">{billTo.contactLines.join('\n')}</p>
              ) : null}
            </div>
            <div className="sm:text-right">
              <p className="text-gray-500">Due Date</p>
              <p className="font-semibold text-gray-900">{formatDate(invoice?.dueDate)}</p>
            </div>
          </div>

          {invoiceScopeStatement ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-amber-700">Billing Scope</p>
              <p className="mt-1">{invoiceScopeStatement}</p>
            </div>
          ) : null}

          <div className="border border-gray-200 rounded-xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Description</th>
                  <th className="text-right px-4 py-3 font-medium">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(invoice.items || []).map((item) => (
                  <tr key={item._id || `${item.description}-${item.amount}`}>
                    <td className="px-4 py-3 text-gray-900">{item.description}</td>
                    <td className="px-4 py-3 text-right text-gray-900">{formatMoney(item.amount)}</td>
                  </tr>
                ))}
                <tr>
                  <td className="px-4 py-3 font-semibold text-gray-700">Amount Due</td>
                  <td className="px-4 py-3 text-right font-bold text-teal-700">{formatMoney(invoice.amountDue)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              <Download size={16} />
              {downloading ? 'Downloading...' : 'Download PDF'}
            </button>
            {invoice?.stripePaymentLink ? (
              <a
                href={invoice.stripePaymentLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-700 text-white text-sm font-medium hover:bg-teal-600"
              >
                <ExternalLink size={16} />
                Pay Invoice
              </a>
            ) : null}
          </div>

          <p className="text-xs text-gray-500">
            Keep this invoice for your records. You can download and save the signed PDF at any time while this link is active.
          </p>
        </div>
      </div>
    </div>
  );
}
